import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { http, formatDateTime } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export default function Overview() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await http.get("/admin/stats")).data });
  const cards = [
    ["Total listings", data?.assets, "/admin/inventory"],
    ["Live on website", data?.published, "/admin/inventory"],
    ["Sold", data?.sold, "/admin/inventory"],
    ["Open inquiries (new / assigned)", data?.new_inquiries, "/admin/inquiries"],
    ["Unassigned inquiries", data?.unassigned, "/admin/inquiries?assigned=unassigned", true],
    ["Overdue follow-ups", data?.overdue, "/admin/inquiries?follow=overdue"],
    ["New sourcing requests", data?.new_sourcing, "/admin/sourcing"],
    ["Reviews awaiting approval", data?.pending_reviews, "/admin/reviews", true],
  ].filter((c) => !c[3] || user?.role === "owner");
  return (
    <div data-testid="admin-overview">
      <AdminTitle title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} sub="Manage inventory, customer requests and follow-ups.">
        <Link to="/admin/inventory/new" className="btn-gold" data-testid="overview-add-asset-button"><Plus className="w-4 h-4" /> Add Asset</Link>
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-px bg-white/10 border hairline">
        {cards.map(([label, v, to]) => (
          <Link key={label} to={to} className="bg-[var(--ink)] p-7 hover:bg-[var(--ink-2)] transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</p>
            <p className="font-display text-5xl mt-3 text-slate-50">{v ?? "–"}</p>
          </Link>
        ))}
      </div>
      <FollowUps />
    </div>
  );
}

const FollowUps = () => {
  const { data } = useQuery({ queryKey: ["followups"], queryFn: async () => (await http.get("/admin/inquiries/followups")).data });
  const groups = [["Overdue follow-ups", data?.overdue || [], "text-rose-300", "border-rose-500/50"], ["Upcoming follow-ups (7 days)", data?.upcoming || [], "text-amber-200", "border-amber-400/40"]];
  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-8" data-testid="overview-followups">
      {groups.map(([title, list, tc, bc]) => (
        <section key={title} className={`border ${bc} bg-[var(--ink-2)] p-6`}>
          <p className={`font-mono text-[0.72rem] uppercase tracking-[0.22em] ${tc}`}>{title} · {list.length}</p>
          {list.length ? (
            <ul className="mt-4 divide-y divide-white/10">
              {list.slice(0, 6).map((i) => (
                <li key={i.id}>
                  <Link to={`/admin/inquiries?id=${i.id}`} className="block py-3 hover:text-gold transition-colors" data-testid={`followup-link-${i.id}`}>
                    <span className="text-sm text-slate-100">{i.name}</span> <span className="text-xs text-slate-400">· {i.asset_title}</span>
                    <span className={`block text-xs mt-1 ${tc}`}>{formatDateTime(i.follow_up_at)}{i.assigned_name ? ` · ${i.assigned_name}` : ""}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-400 mt-4">Nothing here.</p>}
        </section>
      ))}
    </div>
  );
};
